import { Select } from 'antd'
import styles from './ActiveChatBubble.module.scss'
import TypeMessage from 'components/TypeMessageContainer/TypeMessage'
import PatientChatBubble from '../../../../components/PatientChatBubble/PatientChatBubble'
import { useEffect, useState, useRef } from 'react'
import {
  ref,
  onValue,
  query,
  orderByKey,
  startAt,
  endBefore,
  limitToLast,
  get,
} from 'firebase/database'
import { useSelector } from 'react-redux'
import { database } from 'utils/firebase'
import FullPageLoader from 'MentalHealth/components/FullPageLoader'
import useScopes from 'hooks/useScopes'
import { useCallback } from 'react'

const ActiveChatBubble = ({ data }) => {
  const [message, setMessage] = useState([])
  const { isAdmin } = useScopes()
  const [time, setTime] = useState('')
  const { currentSession } = useSelector((state) => state.mentalHealth)
  const { me } = useSelector((state) => state.app)
  const [loading, setLoading] = useState(true)
  const [messageLoading, setMessageLoading] = useState(false)
  const messagesDiv = useRef(null)
  const [sessions, setSessions] = useState()
  const previousKeyRef = useRef(null)

  useEffect(() => {
    if (currentSession?.conversationID) {
      fetchSessions()
    }
  }, [currentSession])

  const fetchSessions = async () => {
    try {
      const snapshot = await get(
        ref(
          database,
          `/sessionMeta/${currentSession.conversationID}/oldSessions`,
        ),
      )

      const sessionsData = snapshot.val()

      if (!sessionsData) {
        setSessions([])
        return
      }

      const sortedSessions = Object.entries(sessionsData).sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      )
      setSessions(sortedSessions)
    } catch (error) {
      console.error('Error fetching sessions:', error)
      setSessions([])
    }
  }


  useEffect(() => {
    const date = new Date(message[0]?.timestamp)

    const options = { month: 'long', day: 'numeric' }
    const formattedDate = date.toLocaleDateString('en-US', options)
    setTime(formattedDate)
  }, [message])

  useEffect(() => {
    if (currentSession?.conversationID && currentSession?.firstMessageID) {
      let chatListener = null
      const getMessages = query(
        ref(
          database,
          `/mentalHealthConversations/${currentSession?.conversationID}`,
        ),
        orderByKey(),
        startAt(currentSession?.firstMessageID),
      )

      chatListener = onValue(getMessages, (snapshot) => {
        const chatData = snapshot.toJSON()
        console.log("chatData", chatData);

        const messageKeys = Object.keys(chatData)
        setLoading(false)
        setMessage(
          messageKeys.map((key) => {
            return {
              ...chatData[key],
              key,
            }
          }),
        )

        previousKeyRef.current = messageKeys[0]
      })

      return () => {
        if (chatListener) {
          chatListener()
        }
      }
    }
  }, [currentSession])

  // fetch archive messages
  const fetchArchiveMessages = async () => {
    if (
      currentSession?.conversationID &&
      currentSession.isArchive &&
      currentSession?.firstMessageID
    ) {
      const getMessages = query(
        ref(
          database,
          `/mentalHealthConversations/${currentSession?.conversationID}`,
        ),
        orderByKey(),
        startAt(currentSession?.firstMessageID),
      )

      const getMessagesSnapshot = await get(getMessages)

      if (getMessagesSnapshot.exists()) {
        const chatData = getMessagesSnapshot.toJSON()
        const messageKeys = Object.keys(chatData)
        setLoading(false)
        setMessage(
          messageKeys.map((key) => ({
            ...chatData[key],
            key,
          })),
        )

        previousKeyRef.current = messageKeys[0]
      }
    }
  }

  useEffect(() => {
    fetchArchiveMessages()
  }, [currentSession])

  const getPreviousSession = useCallback(async () => {
    if (currentSession?.conversationID) {
      const getMessages = query(
        ref(
          database,
          `/mentalHealthConversations/${currentSession?.conversationID}`,
        ),
        orderByKey(),
        endBefore(previousKeyRef.current),
        limitToLast(100),
      )

      const snapshot = await get(getMessages)

      if (snapshot.exists()) {
        const data = snapshot.toJSON()

        const messageKeys = Object.keys(data)

        setMessage((oldMessages) => [
          ...messageKeys.map((key) => ({
            ...data[key],
            key,
          })),
          ...oldMessages,
        ])

        previousKeyRef.current = messageKeys[0]
      }

      setMessageLoading(false)
    }
  }, [currentSession])

  useEffect(() => {
    const handleScroll = () => {
      if (messagesDiv.current && messagesDiv.current.scrollTop === 0) {
        getPreviousSession()
        setMessageLoading(true)
      }
    }

    const attachScrollListener = () => {
      if (messagesDiv.current) {
        messagesDiv.current.addEventListener('scroll', handleScroll)
      } else {
        console.warn('messagesDiv.current is null, retrying...')
      }
    }
    const timeoutId = setTimeout(attachScrollListener, 100)

    return () => {
      if (messagesDiv.current) {
        messagesDiv.current.removeEventListener('scroll', handleScroll)
      }
      clearTimeout(timeoutId)
    }
  }, [messagesDiv.current, getPreviousSession])

  if (loading) return <FullPageLoader />

  return (
    <>
      <div className={styles.chatMessages}>
        <div
          className={isAdmin ? styles.adminMessages : styles.messages}
          ref={messagesDiv}
          id="messages"
        >
          {messageLoading && <FullPageLoader />}

          <div className={styles.timeDiv}>
            <hr />
            <span className={styles.time}>{time}</span>
            <hr />
          </div>

          {message.length > 0 &&
            message.map((obj) => {
              // Common props for both patient and doctor messages
              const chatBubbleProps = {
                data: obj,
                sessions: sessions,
              };

              // Add patient prop only for patient messages
              if (!obj.doctorName) {
                return (
                  <PatientChatBubble
                    key={obj.timestamp}
                    {...chatBubbleProps}
                    patient={true}
                    data={obj}
                    sessions={sessions}
                  />
                );
              } else {
                return (
                  <PatientChatBubble
                    key={obj.timestamp}
                    {...chatBubbleProps}
                    data={obj}
                    sessions={sessions}
                  />
                );
              }
            })}
        </div>

        {!isAdmin && <TypeMessage />}
      </div>
    </>
  )
}

export default ActiveChatBubble
